import { toAbsoluteUrl } from '@/_metronic/helpers';
import {
  type ChecklistAnswer,
  type ChecklistOption,
  ChecklistOrigin,
  ChecklistPhotoSource,
  ChecklistQuestionType,
  type ChecklistQuestion,
  type ChecklistRun,
  ChecklistRunStatus,
  type ChecklistSection,
  type ChecklistTemplate,
} from './_models';
import {
  buildChecklistAnswer,
  countAnsweredQuestions,
  countChecklistQuestions,
  findChecklistQuestion,
  scoreChecklist,
} from './_utils';

export const MOCK_CHECKLIST_STORES = ['Loja Renascença', 'Loja Calhau', 'Loja Cohama'];

export const MOCK_CHECKLIST_SIGNATURE_URL =
  'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="240" height="80"><path d="M10 55 C40 5 60 75 90 35 S140 15 170 45 S210 40 230 25" fill="none" stroke="black" stroke-width="3"/></svg>';

export const MOCK_PHOTO_URL = `data:image/svg+xml;utf8,${encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='320' height='240' viewBox='0 0 320 240'><rect width='320' height='240' fill='#f1f1f4'/><text x='160' y='130' font-family='Arial,sans-serif' font-size='32' fill='#99a1b7' text-anchor='middle'>Foto</text></svg>")}`;

export const DEFAULT_ACTOR = 'Marina Costa';

const YES_NO_OPTIONS: ChecklistOption[] = [
  { value: 'Sim', label: 'Sim', points: 10, problem: false },
  { value: 'Não', label: 'Não', points: 0, problem: true },
];

const INVERTED_YES_NO_OPTIONS: ChecklistOption[] = [
  { value: 'Sim', label: 'Sim', points: 0, problem: true },
  { value: 'Não', label: 'Não', points: 10, problem: false },
];

const QUALITY_OPTIONS: ChecklistOption[] = [
  { value: 'Ótimo', label: 'Ótimo', points: 10, problem: false },
  { value: 'Bom', label: 'Bom', points: 8, problem: false },
  { value: 'Regular', label: 'Regular', points: 6, problem: false },
  { value: 'Ruim', label: 'Ruim', points: 4, problem: true },
  { value: 'Péssimo', label: 'Péssimo', points: 0, problem: true },
];

type QuestionExtras = {
  required?: boolean;
  photoRequired?: boolean;
  allowsNotApplicable?: boolean;
  hint?: string;
  planogramLabel?: string;
  unit?: string;
  expectedRange?: string;
  options?: ChecklistOption[];
};

function optionsOf(type: ChecklistQuestionType): ChecklistOption[] {
  if (type === ChecklistQuestionType.YES_NO) return YES_NO_OPTIONS;
  if (type === ChecklistQuestionType.QUALITY) return QUALITY_OPTIONS;
  return [];
}

function question(
  number: string,
  text: string,
  type: ChecklistQuestionType,
  extras: QuestionExtras = {},
): ChecklistQuestion {
  return {
    id: number,
    number,
    text,
    type,
    required: true,
    photoRequired: false,
    allowsNotApplicable: false,
    options: optionsOf(type),
    ...extras,
  };
}

function yesNo(number: string, text: string, extras: QuestionExtras = {}): ChecklistQuestion {
  return question(number, text, ChecklistQuestionType.YES_NO, extras);
}

function section(index: number, title: string, questions: ChecklistQuestion[]): ChecklistSection {
  return { id: `secao-${index}`, title, questions };
}

const ABASTECIMENTO: ChecklistTemplate = {
  id: 'abastecimento',
  name: 'Checklist de abastecimento',
  coverUrl: toAbsoluteUrl('media/checklists/corredor.jpg'),
  estimatedMinutes: 12,
  sections: [
    section(1, 'Entrada e fachada', [
      yesNo('1.1', 'A fachada e a porta de entrada estão limpas?', { photoRequired: true }),
      yesNo('1.2', 'A porta de acesso abre normalmente?'),
    ]),
    section(2, 'Gôndolas e preços', [
      yesNo('2.1', 'Todos os produtos expostos têm etiqueta de preço?'),
      yesNo('2.2', 'O preço da etiqueta confere com o do autoatendimento?', {
        hint: 'Confira 5 produtos escolhidos ao acaso.',
      }),
      yesNo('2.3', 'As gôndolas ficaram abastecidas conforme o planograma?', {
        photoRequired: true,
        planogramLabel: 'Ver planograma das gôndolas',
      }),
    ]),
    section(3, 'Geladeiras e freezers', [
      question('3.1', 'Qual a temperatura do freezer?', ChecklistQuestionType.NUMBER, {
        unit: '°C',
        expectedRange: 'O esperado é entre -22 °C e -15 °C.',
      }),
      yesNo('3.2', 'As geladeiras ficaram abastecidas conforme o planograma?', {
        photoRequired: true,
        hint: 'Fotografe cada geladeira de frente, com a porta fechada.',
        planogramLabel: 'Ver planograma das geladeiras',
      }),
      yesNo('3.3', 'Há produtos vencidos ou vencendo em até 3 dias?', {
        hint: 'Se houver, retire da exposição e diga quais no comentário.',
        options: INVERTED_YES_NO_OPTIONS,
      }),
    ]),
    section(4, 'Autoatendimento', [
      yesNo('4.1', 'O totem de autoatendimento está ligado e vendendo?'),
      yesNo('4.2', 'A maquininha de cartão está funcionando?', {
        allowsNotApplicable: true,
        hint: 'Lojas sem maquininha marcam "Não se aplica".',
      }),
    ]),
    section(5, 'Limpeza e saída', [
      question('5.1', 'Como está a limpeza da loja?', ChecklistQuestionType.QUALITY),
      yesNo('5.2', 'O lixo foi recolhido?'),
      question('5.3', 'Tire uma foto geral da loja ao sair', ChecklistQuestionType.PHOTO),
      question('5.4', 'Observações da visita', ChecklistQuestionType.TEXT, { required: false }),
    ]),
  ],
};

const ABERTURA: ChecklistTemplate = {
  id: 'abertura',
  name: 'Abertura da loja',
  coverUrl: toAbsoluteUrl('media/checklists/caixa.jpg'),
  estimatedMinutes: 6,
  sections: [
    section(1, 'Chegada', [
      yesNo('1.1', 'O alarme foi desligado sem ocorrências?'),
      yesNo('1.2', 'As luzes da loja estão acesas?'),
      yesNo('1.3', 'O piso está limpo e seco?', { photoRequired: true }),
    ]),
    section(2, 'Equipamentos', [
      yesNo('2.1', 'O totem de autoatendimento está ligado?'),
      yesNo('2.2', 'A maquininha de cartão está funcionando?', { allowsNotApplicable: true }),
      question('2.3', 'Qual a temperatura da geladeira?', ChecklistQuestionType.NUMBER, {
        unit: '°C',
        expectedRange: 'O esperado é entre 2 °C e 8 °C.',
      }),
    ]),
    section(3, 'Exposição', [
      yesNo('3.1', 'Os produtos estão organizados nas gôndolas?'),
      question('3.2', 'Como está a aparência geral da loja?', ChecklistQuestionType.QUALITY),
    ]),
  ],
};

const FECHAMENTO: ChecklistTemplate = {
  id: 'fechamento',
  name: 'Fechamento da loja',
  coverUrl: toAbsoluteUrl('media/checklists/caixa.jpg'),
  estimatedMinutes: 8,
  sections: [
    section(1, 'Limpeza', [
      yesNo('1.1', 'O lixo foi recolhido?'),
      yesNo('1.2', 'O piso foi varrido?'),
      question('1.3', 'Como está a limpeza das geladeiras?', ChecklistQuestionType.QUALITY),
    ]),
    section(2, 'Equipamentos', [
      yesNo('2.1', 'As geladeiras e os freezers estão fechados e ligados?'),
      question('2.2', 'Qual a temperatura do freezer?', ChecklistQuestionType.NUMBER, {
        unit: '°C',
        expectedRange: 'O esperado é entre -22 °C e -15 °C.',
      }),
      yesNo('2.3', 'O totem de autoatendimento foi desligado?'),
    ]),
    section(3, 'Saída', [
      yesNo('3.1', 'O alarme foi ativado?'),
      question('3.2', 'Foto da loja fechada', ChecklistQuestionType.PHOTO),
    ]),
  ],
};

const SEMANAL: ChecklistTemplate = {
  id: 'semanal',
  name: 'Checklist semanal da loja',
  coverUrl: toAbsoluteUrl('media/checklists/frutas.jpg'),
  estimatedMinutes: 20,
  sections: [
    section(1, 'Estrutura', [
      yesNo('1.1', 'A fachada está sem pichações ou danos?', { photoRequired: true }),
      yesNo('1.2', 'A iluminação interna está completa?'),
      yesNo('1.3', 'O piso e as paredes estão sem rachaduras?'),
      yesNo('1.4', 'O ar-condicionado está funcionando?', { allowsNotApplicable: true }),
      yesNo('1.5', 'Os extintores estão dentro da validade?'),
    ]),
    section(2, 'Equipamentos', [
      yesNo('2.1', 'O totem de autoatendimento está sem falhas?'),
      yesNo('2.2', 'A maquininha de cartão está funcionando?', { allowsNotApplicable: true }),
      question('2.3', 'Qual a temperatura do freezer?', ChecklistQuestionType.NUMBER, {
        unit: '°C',
        expectedRange: 'O esperado é entre -22 °C e -15 °C.',
      }),
      question('2.4', 'Qual a temperatura da geladeira?', ChecklistQuestionType.NUMBER, {
        unit: '°C',
        expectedRange: 'O esperado é entre 2 °C e 8 °C.',
      }),
      yesNo('2.5', 'As câmeras estão gravando?'),
    ]),
    section(3, 'Produtos', [
      yesNo('3.1', 'Há produtos vencidos na exposição?', { options: INVERTED_YES_NO_OPTIONS }),
      yesNo('3.2', 'As gôndolas seguem o planograma?', {
        photoRequired: true,
        planogramLabel: 'Ver planograma das gôndolas',
      }),
      yesNo('3.3', 'Todos os produtos têm etiqueta de preço?'),
      question('3.4', 'Como está a organização do estoque?', ChecklistQuestionType.QUALITY),
    ]),
    section(4, 'Limpeza e fechamento', [
      question('4.1', 'Como está a limpeza geral da loja?', ChecklistQuestionType.QUALITY),
      yesNo('4.2', 'O lixo é recolhido todos os dias?'),
      yesNo('4.3', 'Os banheiros estão limpos?', { allowsNotApplicable: true }),
      question('4.4', 'Foto geral da loja', ChecklistQuestionType.PHOTO),
    ]),
  ],
};

const VALIDADE: ChecklistTemplate = {
  id: 'validade',
  name: 'Conferência de validade',
  coverUrl: toAbsoluteUrl('media/checklists/frutas.jpg'),
  estimatedMinutes: 10,
  sections: [
    section(1, 'Geladeiras', [
      yesNo('1.1', 'Há produtos vencidos nas geladeiras?', {
        hint: 'Olhe a data de cada embalagem.',
        options: INVERTED_YES_NO_OPTIONS,
      }),
      yesNo('1.2', 'Há produtos vencendo em até 3 dias?', { options: INVERTED_YES_NO_OPTIONS }),
      question('1.3', 'Quantos produtos foram retirados?', ChecklistQuestionType.NUMBER, {
        unit: 'un',
        required: false,
      }),
    ]),
    section(2, 'Gôndolas', [
      yesNo('2.1', 'Há produtos vencidos nas gôndolas?', { options: INVERTED_YES_NO_OPTIONS }),
      question('2.2', 'Foto dos produtos retirados', ChecklistQuestionType.PHOTO, { required: false }),
      question('2.3', 'Observações', ChecklistQuestionType.TEXT, { required: false }),
    ]),
  ],
};

const AUDITORIA: ChecklistTemplate = {
  id: 'auditoria',
  name: 'Auditoria de loja',
  coverUrl: toAbsoluteUrl('media/checklists/caixa.jpg'),
  estimatedMinutes: 25,
  sections: [
    section(1, 'Padrão visual', [
      yesNo('1.1', 'A fachada segue o padrão da marca?', { photoRequired: true }),
      yesNo('1.2', 'A comunicação visual está atualizada?'),
      question('1.3', 'Como está a conservação da loja?', ChecklistQuestionType.QUALITY),
    ]),
    section(2, 'Operação', [
      yesNo('2.1', 'O mix de produtos segue o definido pela franqueadora?'),
      yesNo('2.2', 'Os preços estão alinhados com a tabela?'),
      question('2.3', 'Qual a temperatura do freezer?', ChecklistQuestionType.NUMBER, {
        unit: '°C',
        expectedRange: 'O esperado é entre -22 °C e -15 °C.',
      }),
    ]),
    section(3, 'Resultado', [
      yesNo('3.1', 'As rotinas de limpeza estão em dia?'),
      question('3.2', 'Foto geral da loja', ChecklistQuestionType.PHOTO),
      question('3.3', 'Recomendações do auditor', ChecklistQuestionType.TEXT, { required: false }),
    ]),
  ],
};

export const MOCK_CHECKLIST_TEMPLATES: ChecklistTemplate[] = [
  ABASTECIMENTO,
  ABERTURA,
  FECHAMENTO,
  SEMANAL,
  VALIDADE,
  AUDITORIA,
];

export function findMockChecklistTemplate(templateId: string): ChecklistTemplate {
  const template = MOCK_CHECKLIST_TEMPLATES.find((item) => item.id === templateId);
  if (!template) throw new Error(`Questionário ${templateId} não existe no mock.`);
  return template;
}

export function checklistMoment(now: Date, dayOffset: number, time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  const moment = new Date(now.getFullYear(), now.getMonth(), now.getDate() + dayOffset, hours, minutes);
  return moment.toISOString();
}

type SeedAnswer = { questionId: string; value?: string; comment?: string; photoCount?: number };

export function minutesBefore(now: Date, minutes: number): string {
  return new Date(now.getTime() - minutes * 60_000).toISOString();
}

function pastDayAnsweredAt(now: Date, dayOffset: number, hour: string) {
  return (index: number) => checklistMoment(now, dayOffset, `${hour}:${String(5 + index).padStart(2, '0')}`);
}

function seedAnswersFor(
  template: ChecklistTemplate,
  answeredAtOf: (index: number) => string,
  seeds: SeedAnswer[],
): ChecklistAnswer[] {
  return seeds.map((seed, index) => {
    const found = findChecklistQuestion(template.sections, seed.questionId);
    if (!found) throw new Error(`Questão ${seed.questionId} não existe no mock.`);
    const answeredAt = answeredAtOf(index);
    const photos = Array.from({ length: seed.photoCount ?? 0 }, (_, photoIndex) => ({
      id: `foto-${seed.questionId}-${photoIndex + 1}`,
      url: MOCK_PHOTO_URL,
      source: ChecklistPhotoSource.CAMERA,
      takenAt: answeredAt,
    }));
    const input = {
      questionId: seed.questionId,
      value: seed.value,
      notApplicable: false,
      comment: seed.comment ?? '',
      photos,
    };
    return buildChecklistAnswer(found.options, input, DEFAULT_ACTOR, answeredAt);
  });
}

function bestAnswerSeeds(template: ChecklistTemplate, overrides: Partial<Record<string, string>> = {}): SeedAnswer[] {
  const questions = template.sections.flatMap((item) => item.questions);
  return questions.map((item) => {
    const photoCount = item.photoRequired || item.type === ChecklistQuestionType.PHOTO ? 1 : 0;
    const bestOption = item.options.find((option) => !option.problem && option.points === 10);
    const textValue = item.type === ChecklistQuestionType.TEXT ? 'Tudo certo.' : undefined;
    const numberValue = item.type === ChecklistQuestionType.NUMBER ? '-18' : undefined;
    const value = overrides[item.id] ?? bestOption?.value ?? textValue ?? numberValue;
    return { questionId: item.id, value, photoCount };
  });
}

function finishedRun(base: ChecklistRun, template: ChecklistTemplate, sentAt: string): ChecklistRun {
  return {
    ...base,
    sentAt,
    sentBy: DEFAULT_ACTOR,
    score: scoreChecklist(template.sections, base.answers),
    checkout: {
      comment: 'Visita concluída.',
      signatureUrl: MOCK_CHECKLIST_SIGNATURE_URL,
      signedBy: DEFAULT_ACTOR,
      signedAt: sentAt,
    },
  };
}

type RunBase = {
  id: string;
  template: ChecklistTemplate;
  store: string;
  origin: ChecklistOrigin;
  status: ChecklistRunStatus;
  dayOffset: number;
  dueTime: string;
  answers?: ChecklistAnswer[];
};

function runOf(
  { id, template, store, origin, status, dayOffset, dueTime, answers = [] }: RunBase,
  now: Date,
): ChecklistRun {
  return {
    id,
    templateId: template.id,
    templateName: template.name,
    coverUrl: template.coverUrl,
    store,
    origin,
    dueAt: checklistMoment(now, dayOffset, dueTime),
    status,
    progress: {
      answered: countAnsweredQuestions(template.sections, answers),
      total: countChecklistQuestions(template.sections),
    },
    answers,
  };
}

export function createMockChecklistRuns(now: Date): ChecklistRun[] {
  const [renascenca, calhau, cohama] = MOCK_CHECKLIST_STORES;

  const semanal = runOf(
    {
      id: 'semanal-cohama',
      template: SEMANAL,
      store: cohama,
      origin: ChecklistOrigin.SCHEDULE,
      status: ChecklistRunStatus.LATE,
      dayOffset: -1,
      dueTime: '20:00',
    },
    now,
  );

  const abastecimento482Answers = seedAnswersFor(ABASTECIMENTO, (index) => minutesBefore(now, 9 - index), [
    { questionId: '1.1', value: 'Sim', photoCount: 1 },
    { questionId: '1.2', value: 'Sim' },
    { questionId: '2.1', value: 'Não', comment: 'Faltavam etiquetas nos biscoitos da gôndola 3; recoloquei.' },
    { questionId: '2.2', value: 'Sim' },
    { questionId: '2.3', value: 'Sim', photoCount: 2 },
    { questionId: '3.1', value: '-18' },
  ]);
  const abastecimento482: ChecklistRun = {
    ...runOf(
      {
        id: 'abastecimento-482',
        template: ABASTECIMENTO,
        store: renascenca,
        origin: ChecklistOrigin.ACTIVITY,
        status: ChecklistRunStatus.IN_PROGRESS,
        dayOffset: 0,
        dueTime: '18:00',
        answers: abastecimento482Answers,
      },
      now,
    ),
    activity: { taskId: 'task-482', label: 'Abastecimento #482', mandatory: true },
    startedAt: minutesBefore(now, 10),
    startedBy: DEFAULT_ACTOR,
  };

  const abastecimento483: ChecklistRun = {
    ...runOf(
      {
        id: 'abastecimento-483',
        template: ABASTECIMENTO,
        store: calhau,
        origin: ChecklistOrigin.ACTIVITY,
        status: ChecklistRunStatus.PENDING,
        dayOffset: 0,
        dueTime: '20:00',
      },
      now,
    ),
    activity: { taskId: 'task-483', label: 'Abastecimento #483', mandatory: true },
  };

  const aberturaAnswers = seedAnswersFor(
    ABERTURA,
    (index) => minutesBefore(now, 129 - index),
    bestAnswerSeeds(ABERTURA),
  );
  const abertura = finishedRun(
    {
      ...runOf(
        {
          id: 'abertura-renascenca',
          template: ABERTURA,
          store: renascenca,
          origin: ChecklistOrigin.SCHEDULE,
          status: ChecklistRunStatus.SENT,
          dayOffset: 0,
          dueTime: '10:00',
          answers: aberturaAnswers,
        },
        now,
      ),
      startedAt: minutesBefore(now, 130),
      startedBy: DEFAULT_ACTOR,
    },
    ABERTURA,
    minutesBefore(now, 120),
  );

  const auditoriaAnswers = seedAnswersFor(
    AUDITORIA,
    pastDayAnsweredAt(now, -3, '14'),
    bestAnswerSeeds(AUDITORIA, { '1.3': 'Ruim', '2.2': 'Não' }),
  );
  const auditoria = finishedRun(
    {
      ...runOf(
        {
          id: 'auditoria-renascenca',
          template: AUDITORIA,
          store: renascenca,
          origin: ChecklistOrigin.FRANCHISOR,
          status: ChecklistRunStatus.SENT,
          dayOffset: -3,
          dueTime: '17:00',
          answers: auditoriaAnswers,
        },
        now,
      ),
      appliedBy: 'Ana Ribeiro · franqueadora',
      startedAt: checklistMoment(now, -3, '14:00'),
      startedBy: DEFAULT_ACTOR,
    },
    AUDITORIA,
    checklistMoment(now, -3, '15:10'),
  );

  const abastecimento474: ChecklistRun = {
    ...runOf(
      {
        id: 'abastecimento-474',
        template: ABASTECIMENTO,
        store: cohama,
        origin: ChecklistOrigin.ACTIVITY,
        status: ChecklistRunStatus.NOT_DONE,
        dayOffset: -4,
        dueTime: '20:00',
      },
      now,
    ),
    activity: { taskId: 'task-474', label: 'Abastecimento #474', mandatory: false },
  };

  const abastecimento470: ChecklistRun = {
    ...runOf(
      {
        id: 'abastecimento-470',
        template: ABASTECIMENTO,
        store: calhau,
        origin: ChecklistOrigin.ACTIVITY,
        status: ChecklistRunStatus.CANCELED,
        dayOffset: -5,
        dueTime: '20:00',
      },
      now,
    ),
    activity: { taskId: 'task-470', label: 'Abastecimento #470', mandatory: true },
  };

  return [semanal, abastecimento482, abastecimento483, abertura, auditoria, abastecimento474, abastecimento470];
}
